import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, atLeast, type Role, type TestSelection } from "../api";

/**
 * The "Tests" list every rig in this project offers in its run form.
 *
 * THREE RULES, all of them from the contract, and all of them about not
 * changing what nobody asked to change:
 *
 *  - SAVE ONLY WHAT CHANGED. While the project uses the service's defaults the
 *    editor shows them, and a save that sent them back unchanged would FREEZE
 *    them: when the defaults move, this project would not follow, and nothing
 *    would say why. So the list is sent only when it differs from what was
 *    loaded.
 *  - GOING BACK IS ITS OWN ACTION, sending `null`. Emptying the rows is not the
 *    same thing and should not look like it.
 *  - THE SERVER JUDGES A MARKER. It checks the syntax with the very validator
 *    the agent on the bench uses - one function, imported, not copied - so a
 *    rule written here as well would be a second copy, and the refusal already
 *    names the marker it refused.
 *
 * Collapsed by default: it is set up once per project and then left alone, and
 * the page it sits on is read daily.
 */
export default function TestSelectionsEditor({
  projectId,
  selections,
  isDefault,
  role,
  onSaved,
}: {
  projectId: number;
  selections: TestSelection[];
  isDefault: boolean;
  role: Role | null;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TestSelection[]>(selections);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const may = atLeast(role, "maintainer");

  // Follow the project when it reloads - after a save, or a revert.
  // ONLY A LIST THE EDITOR HAS NOT SEEN replaces the rows. The effect also runs
  // on mount, with the very list the rows were started from, and on a slow
  // machine that late copy landed after somebody's first keystrokes and erased
  // them - the Projects test failed exactly so in CI (2026-09-14). A reload
  // that brings back the same list keeps what is being typed, too.
  const seen = useRef(JSON.stringify(selections));
  useEffect(() => {
    const incoming = JSON.stringify(selections);
    if (incoming === seen.current) return;
    seen.current = incoming;
    setRows(selections);
  }, [selections]);

  const changed = JSON.stringify(rows) !== JSON.stringify(selections);
  const blank = rows.some((r) => !r.marker.trim() || !r.label.trim());

  async function save(next: TestSelection[] | null) {
    setBusy(true);
    setError("");
    try {
      await api.updateProject(projectId, {
        test_selections: next === null ? null : next.map((r) => ({
          marker: r.marker.trim(),
          label: r.label.trim(),
        })),
      });
      onSaved();
    } catch (e) {
      // The server's words: it names the marker it would not take.
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const set = (i: number, patch: Partial<TestSelection>) =>
    setRows(rows.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };

  return (
    <details className="mt-6">
      <summary className="cursor-pointer text-base font-semibold text-slate-800 dark:text-slate-200">
        {t("testSelections.title")}
        <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
          {isDefault
            ? t("testSelections.usingDefaults")
            : t("testSelections.count", { count: selections.length })}
        </span>
      </summary>

      <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-xs text-slate-500">{t("testSelections.what")}</p>

        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <input
              className="min-w-[12rem] flex-1 rounded border border-slate-300 px-2 py-1 font-mono text-xs dark:border-slate-700 dark:bg-slate-800"
              value={r.marker}
              placeholder="auto"
              aria-label={t("testSelections.marker")}
              disabled={!may}
              onChange={(e) => set(i, { marker: e.target.value })}
            />
            <input
              className="min-w-[14rem] flex-[2] rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-800"
              value={r.label}
              placeholder={t("testSelections.labelPlaceholder")}
              aria-label={t("testSelections.label")}
              disabled={!may}
              onChange={(e) => set(i, { label: e.target.value })}
            />
            {may && (
              <span className="flex gap-1 text-slate-500">
                <button type="button" aria-label={t("testSelections.up")} className="px-1"
                        onClick={() => move(i, -1)}>↑</button>
                <button type="button" aria-label={t("testSelections.down")} className="px-1"
                        onClick={() => move(i, 1)}>↓</button>
                <button type="button" aria-label={t("testSelections.remove")} className="px-1"
                        onClick={() => setRows(rows.filter((_, j) => j !== i))}>×</button>
              </span>
            )}
          </div>
        ))}

        {error && (
          <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
            {error}
          </p>
        )}

        {may ? (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              className="rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-700"
              onClick={() => setRows([...rows, { marker: "", label: "" }])}
            >
              {t("testSelections.add")}
            </button>
            <button
              type="button"
              className="rounded bg-brand-700 px-3 py-1 text-sm font-medium text-white disabled:opacity-40"
              disabled={busy || !changed || blank || rows.length === 0}
              onClick={() => save(rows)}
            >
              {busy ? t("testSelections.saving") : t("testSelections.save")}
            </button>
            {!isDefault && (
              <button
                type="button"
                className="px-2 py-1 text-sm text-slate-500 underline"
                disabled={busy}
                onClick={() => save(null)}
              >
                {t("testSelections.useDefaults")}
              </button>
            )}
            {changed && blank && (
              <span className="text-xs text-slate-500">{t("testSelections.blank")}</span>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500">{t("testSelections.needMaintainer")}</p>
        )}
      </div>
    </details>
  );
}
